import {
  applyCollabOps,
  diffScreenDesign,
  ESdChangeReason,
  ICollabOp,
  invertCollabOps,
  IScreenDesign
} from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { collabIdsEqual } from "../../../services/collab/collab-id";
import { CollabService } from "../../../services/collab/collab-service";
import {
  CollabRemoteChangeKind,
  CollabSessionCore,
  COLLAB_CLIENT_ID,
  ICollabIdRemap,
  makeCollabOpId
} from "../../../services/collab/collab-session-core";
import { ICollabParticipant, ICollabSyncResponse } from "../../../services/collab/collab-types";
import { sdOptimisticIndexOf } from "../../../services/collab/sd-optimistic-index";
import { IDesignerPresence } from "../../../services/designer-live/designer-live-types";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

/** Reasons that are never a local tree mutation — no diff, no op emission. */
const NON_TREE_REASONS: ReadonlySet<ESdChangeReason> = new Set<ESdChangeReason>([
  ESdChangeReason.Loading,
  ESdChangeReason.Terminate,
  ESdChangeReason.LoadProject,
  ESdChangeReason.ScreenReload,
  ESdChangeReason.ExpandedProperties,
  ESdChangeReason.OtherSetStates,
  ESdChangeReason.RemoteCollabOp,
  ESdChangeReason.RemoteResync
]);

const SELECTION_REASONS: ReadonlySet<ESdChangeReason> = new Set<ESdChangeReason>([
  ESdChangeReason.ChangeSelectedDiByTools,
  ESdChangeReason.ChangeSelectedDiByCanvasFrame
]);

export interface CollabProviderState {
  collabSessionActive: boolean;
  collabIsWriter: boolean;
  collabRoster: ICollabParticipant[];
  /** Faz 5: project-level presence (who is in which tool/entity) from the designer-live stream. */
  designerRoster: IDesignerPresence[];
  joinCollabSession: (screenId: string) => Promise<void>;
  leaveCollabSession: () => void;
  /**
   * Suspend session participation for a bulk server-side write (agent build, workflow Forms tab
   * Apply Screens). Resolves when the server-side Leave has landed — await it before triggering
   * the write, or the backend's live-participant guard will still see this tab.
   */
  suspendCollabForAgentBuild: () => Promise<void>;
  /** Resume normal participation and rejoin the screen the canvas is on. */
  resumeCollabAfterAgentBuild: (screenId: string) => Promise<void>;
}

/**
 * Screen-domain face of real-time collaboration — P2: FULL CONCURRENT EDITING. The
 * entity-agnostic session machinery (SSE stream + reconnect + gap replay, op queue with
 * coalesced flushing, seq-aware LWW / structural-order ack processing, per-user undo
 * stacks, presence throttling — see CollabSessionCore) is parameterized here with the
 * sd-patch engine: local mutations diff against the core's shadow tree via
 * `diffScreenDesign`, remote/accepted ops apply via `applyCollabOps`, inverses come from
 * `invertCollabOps`, and remote commits dispatch back through the screen provider.
 *
 * Invariants (enforced by the core; proven by the sd-patch convergence simulation):
 * - shadowTree always equals "live tree minus my unsent local edits" — local diffs are
 *   computed against it, remote ops are applied to it, so a remote batch arriving between
 *   two local gestures can never be misread as a local change.
 * - Only server-accepted ops mutate other clients; a reject (or seq gap) triggers a full
 *   resync from the authoritative state rather than any local repair heuristics.
 * - Undo/redo is DISABLED while others are in the session (snapshot undo would revert
 *   their work); alone in the session it works normally and its effects sync as ops.
 */
export class CollabProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  CollabProviderState
> {
  static defaultState: CollabProviderState = {
    collabSessionActive: false,
    collabIsWriter: false,
    collabRoster: [],
    designerRoster: [],
    joinCollabSession: async () => {},
    leaveCollabSession: () => {},
    suspendCollabForAgentBuild: async () => {},
    resumeCollabAfterAgentBuild: async () => {}
  };

  private core: CollabSessionCore<IScreenDesign, ICollabOp> = new CollabSessionCore<IScreenDesign, ICollabOp>({
    entityRoute: "screen",
    adapter: {
      getSerializableDoc: (): IScreenDesign | null => {
        // CROSS-PARTITION METHODS MUST GO THROUGH this.partial — this.state holds the
        // partition defaultState STUBS (the real functions live only in the context value);
        // reading via this.state returned {} here and seeded EMPTY sessions.
        const tree: IScreenDesign = this.partial.screenProvider.getSerializableScreenDesign();
        if (!tree || !tree.id) return null;
        return tree;
      },
      getLiveDoc: (): IScreenDesign => this.partial.screenProvider.getScreenDesign(),
      getDocId: (doc: IScreenDesign): string | null => (doc?.id != null && doc.id !== "" ? String(doc.id) : null),
      diff: (before: IScreenDesign, after: IScreenDesign): ICollabOp[] =>
        diffScreenDesign(before, after, makeCollabOpId),
      invert: (before: IScreenDesign, ops: ICollabOp[]): ICollabOp[] => invertCollabOps(before, ops, makeCollabOpId),
      applyOps: (doc: IScreenDesign, ops: ICollabOp[]): ICollabOp[] => applyCollabOps(doc, ops).skippedOps,
      commitRemoteDoc: (doc: IScreenDesign, kind: CollabRemoteChangeKind): void => {
        const reason: ESdChangeReason =
          kind === "resync" ? ESdChangeReason.RemoteResync : ESdChangeReason.RemoteCollabOp;
        this.partial.screenProvider.setScreenDesign(doc, reason);
      },
      parseSnapshot: (json: string): IScreenDesign | null => {
        const sd: IScreenDesign = JSON.parse(json) as IScreenDesign;
        if (!sd || !sd.id) return null;
        return sd;
      },
      lwwKeyOf: (op: ICollabOp): string | null => lwwKeyOf(op),
      isStructural: (op: ICollabOp): boolean => isStructuralOp(op),
      coalesce: (ops: ICollabOp[]): ICollabOp[] => coalescePropSets(ops),
      remintForReplay: (op: ICollabOp): ICollabOp => ({ ...op, opId: makeCollabOpId(), resolvedIndex: undefined }),
      makeResolvedIndexCorrection: (op: ICollabOp, resolvedIndex: number): ICollabOp | null => {
        if (op.kind !== "nodeAdd" && op.kind !== "nodeMove") return null;
        const movedId: string =
          op.kind === "nodeMove" ? (op.nodeId ?? "") : String((op.nodePayload as IRemappableNode)?.id ?? "");
        if (!movedId) return null;
        return {
          opId: makeCollabOpId(),
          kind: "nodeMove",
          nodeId: movedId,
          parentId: op.parentId,
          resolvedIndex
        };
      },
      optimisticIndexOf: (doc: IScreenDesign, op: ICollabOp): number | null => sdOptimisticIndexOf(doc, op),
      // A skipped remote nodeAdd means its id collides with one of our optimistic local nodes.
      skippedOpForcesResync: (op: ICollabOp): boolean => op.kind === "nodeAdd",
      remapIds: (doc: IScreenDesign, idRemap: ICollabIdRemap): void => remapIdsInTree(doc, idRemap)
    },
    host: {
      onSessionJoined: (roster: ICollabParticipant[]): void => {
        this.setState({
          collabSessionActive: true,
          collabIsWriter: true,
          collabRoster: roster
        });
      },
      onSessionLeft: (): void => {
        if (this.state.collabSessionActive) {
          this.setState({ collabSessionActive: false, collabIsWriter: false, collabRoster: [] });
        }
      },
      applyRosterUpdate: (roster: ICollabParticipant[]): void => {
        this.applyRosterUpdate(roster);
      },
      getRoster: (): ICollabParticipant[] => this.state.collabRoster,
      getSelectedIds: (): string[] => (this.state.selectedKuikaIds ?? []).map((id: string | number) => String(id))
    }
  });

  getProviderValues = (): CollabProviderState => {
    return {
      collabSessionActive: this.state.collabSessionActive,
      collabIsWriter: this.state.collabIsWriter,
      collabRoster: this.state.collabRoster,
      designerRoster: this.state.designerRoster,
      joinCollabSession: this.joinCollabSession,
      suspendCollabForAgentBuild: this.suspendCollabForAgentBuild,
      resumeCollabAfterAgentBuild: this.resumeCollabAfterAgentBuild,
      leaveCollabSession: this.leaveCollabSession
    };
  };

  /** Faz 5: fed by the designer-live stream (project presence roster). */
  public setDesignerRoster = (roster: IDesignerPresence[]): void => {
    this.setState({ designerRoster: roster ?? [] });
  };

  public isCollabSessionActive = (): boolean => {
    return this.core.isSessionActive();
  };

  /** The screen this tab's session actually targets — every save funnel must compare against it. */
  public getSessionScreenId = (): string | null => {
    return this.core.getSessionEntityId();
  };

  /**
   * Screen switch (KOA-18173): flush the DEPARTING screen's pending ops into ITS session,
   * then leave — before the new screen's tree is committed, so no save can ever pair the
   * old session id with the new screen's payload.
   */
  public flushAndLeaveCollabSession = async (): Promise<void> => {
    await this.core.flushAndLeave();
  };

  /** True when someone ELSE is in the session — the undo/redo gate. */
  public hasOtherParticipants = (): boolean => {
    return (
      this.core.isSessionActive() &&
      this.state.collabRoster.some((p: ICollabParticipant) => p.clientId !== COLLAB_CLIENT_ID)
    );
  };

  // ---- lifecycle ------------------------------------------------------------------------

  /**
   * KOA-18030 — an agent build must not become its own blocker.
   *
   * Every screen load joins a session (`openProject` → `joinCollabSession`), so a
   * 3-screen AI build opened 3 sessions on its own tab. The backend then refused
   * that same build's whole-screen PUT — "Screen [X] is being edited in a live
   * collaboration session" — because a session with ≥1 participant exists; the
   * guard counts PARTICIPANTS, not distinct humans. The reload that follows the
   * rejected save replaced the built tree with the stored (empty) copy, so the
   * renders reported success and the canvas came back bare (telemetry session
   * c331cd00-e8d2-454c-8a18-237c7bd9f571).
   *
   * While suspended this tab does not join and does not create a session. What
   * that leaves behind is exactly the protection worth keeping: if ANOTHER person
   * is editing the screen, THEIR session is still live, the whole-screen PUT is
   * still rejected, and (since `840b30ef8d`) the agent is told so instead of
   * silently losing the work. Alone, there is no session and nothing to protect.
   */
  private agentBuildSuspended: boolean = false;

  joinCollabSession = async (screenId: string): Promise<void> => {
    if (!screenId || screenId === "0") return;
    if (this.agentBuildSuspended) return;
    await this.core.join(screenId);
  };

  suspendCollabForAgentBuild = async (): Promise<void> => {
    if (this.agentBuildSuspended) return;
    this.agentBuildSuspended = true;
    // Leave any session this tab already holds — the screen the user had open before starting
    // the build is the first one the agent (or the workflow Forms tab's Apply Screens regen)
    // will write to. Pending local ops are flushed first so nothing is dropped, and the promise
    // settles only when the server-side Leave has landed — the backend's collab guard checks for
    // live participants, so the caller must be able to await the membership actually being gone.
    await this.core.flushAndLeave();
  };

  /**
   * Is an agent build in progress on THIS tab?
   *
   * The flag was introduced so a build stops adding itself to the collab participant count.
   * It is also the only in-tab signal that "the AI is mid-build", and a second consumer
   * needs exactly that: the Issues panel's validate-on-open. A build makes dozens of
   * mutations, each one marking the last validation stale, so opening the panel during one
   * starts a full project validation (~18s typical, 83s worst, and it fans out to customer
   * datasources) — while the build's OWN verify edge is going to force another at the end.
   * The forced run deliberately never joins an older one, so the two overlap.
   *
   * Read-only by design: only the build's own suspend/resume owns the flag.
   */
  isAgentBuildInProgress = (): boolean => this.agentBuildSuspended;

  resumeCollabAfterAgentBuild = async (screenId: string): Promise<void> => {
    if (!this.agentBuildSuspended) return;
    this.agentBuildSuspended = false;
    if (!screenId || screenId === "0") return;
    await this.core.join(screenId);
  };

  leaveCollabSession = (): void => {
    void this.core.leave();
  };

  // ---- local mutations → ops ---------------------------------------------------------------

  /** Called from screen-provider.setScreenData after EVERY screen-data commit. */
  public onScreenDataChanged = (reason: ESdChangeReason): void => {
    if (!this.core.isSessionActive() || this.core.isApplyingRemoteChange()) return;

    if (SELECTION_REASONS.has(reason)) {
      this.core.scheduleSelectionBroadcast();
      return;
    }
    if (NON_TREE_REASONS.has(reason)) return;

    this.core.emitLocalOps();
  };

  // ---- per-user undo/redo (multi-participant sessions) ------------------------------------

  public canUndoCollab = (): boolean => this.core.canUndo();

  public canRedoCollab = (): boolean => this.core.canRedo();

  public undoLocalOp = (): void => {
    this.core.undo();
  };

  public redoLocalOp = (): void => {
    this.core.redo();
  };

  // ---- presence -----------------------------------------------------------------------------------

  private applyRosterUpdate = (roster: ICollabParticipant[]): void => {
    this.setState({ collabRoster: roster, collabIsWriter: true });
    const remoteSelections = roster
      .filter((p) => p.clientId !== COLLAB_CLIENT_ID && ((p.selectedIds?.length ?? 0) > 0 || p.cursor != null))
      .map((p) => ({
        clientId: p.clientId,
        userName: p.userName,
        color: p.color,
        kuikaIds: p.selectedIds ?? [],
        cursor: p.cursor ?? undefined
      }));
    this.partial.frame.sendRemoteUserSelections({ selections: remoteSelections });
  };

  /** Live cursor from the canvas iframe (already 80ms-throttled agent-side). */
  public onLocalCursorMoved = (x: number, y: number): void => {
    this.core.onLocalCursorMoved(x, y);
  };

  // ---- save ---------------------------------------------------------------------------------------

  /**
   * Explicit/auto save in collab mode: the tree already flows through ops — only the
   * metadata (publicJson / screen name) needs pushing; the server persists from its
   * authoritative state on its flush cadence. targetScreenId is the screen the payload
   * was BUILT for — a mismatch with the session means a screen switch is in progress and
   * syncing would stamp another screen's name/publicJson onto this session's row
   * (KOA-18173); the caller then falls back to the legacy PUT with the correct id.
   *
   * `publicJson` is optional because the save omits it when the screen's own metadata is
   * unavailable — CollabSession.SetMetadata already treats an absent blob as "leave the
   * stored metadata alone", which is exactly the intent (see processSaveScreen).
   */
  public saveThroughCollab = async (
    targetScreenId: string,
    publicJson: string | undefined,
    name: string
  ): Promise<boolean> => {
    const sessionScreenId: string | null = this.core.getSessionEntityId();
    if (!sessionScreenId) return false;
    if (!collabIdsEqual(targetScreenId, sessionScreenId)) {
      return false;
    }
    try {
      // In-place tree mutations that never dispatched (a rename writes tree.name directly)
      // become seq-protected ops before the metadata sync — the flush then persists the
      // name from the session TREE, the only LWW-safe channel.
      this.core.emitLocalOps();
      // Make sure everything the user just did is on the wire before declaring "saved".
      await this.core.flushOps();
      const res: AxiosResponse<ICollabSyncResponse> = await CollabService.Sync(
        sessionScreenId,
        COLLAB_CLIENT_ID,
        publicJson,
        name,
        targetScreenId
      );
      return !!res && res.status === 200;
    } catch (error: unknown) {
      const status: number | undefined = (error as ICollabHttpErrorLike)?.response?.status;
      if (status === 404 || status === 403) {
        // The server has no such session (restart, or our participant expired) — the sync
        // could never have landed. Reclaim the session out of band; returning false lets
        // the caller persist through the legacy PUT so this save is not lost either way
        // (KOA-18256: the whole tree stayed unsaved while the save reported success).
        this.core.reclaimAfterSessionLoss("sync");
      }
      return false;
    }
  };
}

// ---- screen-domain module helpers --------------------------------------------------------------------

function isStructuralOp(op: ICollabOp): boolean {
  return op.kind === "nodeAdd" || op.kind === "nodeRemove" || op.kind === "nodeMove";
}

/** The LWW conflict key of an op: property path for prop writes, node identity for moves. */
function lwwKeyOf(op: ICollabOp): string | null {
  if (op.kind === "propSet" || op.kind === "propRemove") return `${op.nodeId}|${op.path}`;
  if (op.kind === "screenPropSet" || op.kind === "screenPropRemove") return `screen|${op.path}`;
  if (op.kind === "nodeMove") return `move|${op.nodeId}`;
  return null;
}

/** Merges successive propSets on the same (nodeId, path) — drag/slider streams — keeping the last value. */
function coalescePropSets(ops: ICollabOp[]): ICollabOp[] {
  const result: ICollabOp[] = [];
  const lastPropSetIndex: Map<string, number> = new Map<string, number>();
  for (const op of ops) {
    if (op.kind === "propSet") {
      const key: string = `${op.nodeId}|${op.path}`;
      const existing: number | undefined = lastPropSetIndex.get(key);
      if (existing !== undefined) {
        result[existing] = op;
        continue;
      }
      lastPropSetIndex.set(key, result.length);
    }
    result.push(op);
  }
  return result;
}

/** Axios rejection shape — CollabService propagates errors rather than swallowing them. */
interface ICollabHttpErrorLike {
  response?: { status?: number };
}

interface IRemappableNode {
  id: number | string;
  parentId?: number | string;
  children?: IRemappableNode[];
}

function remapIdsInTree(tree: IScreenDesign, idRemap: ICollabIdRemap): void {
  const walk = (nodes: IRemappableNode[] | undefined): void => {
    if (!nodes) return;
    for (const node of nodes) {
      const mappedId: string | undefined = idRemap[String(node.id)];
      if (mappedId !== undefined) node.id = Number(mappedId);
      const mappedParent: string | undefined = node.parentId !== undefined ? idRemap[String(node.parentId)] : undefined;
      if (mappedParent !== undefined) node.parentId = Number(mappedParent);
      walk(node.children);
    }
  };
  walk(tree.displayItems as unknown as IRemappableNode[]);
}
